// src/pages/EmployeeReport.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Badge,
  InputGroup,
} from "react-bootstrap";
import API from "../services/api";

/* ---------------- helpers ---------------- */
const locationText = (req) => {
  if (typeof req.locationText === "string" && req.locationText.trim()) return req.locationText.trim();
  const loc = req.location;
  if (!loc) return "N/A";
  if (typeof loc === "string") return loc.trim() || "N/A";
  const { address, line1, area, city, state, pincode, lat, lng, latitude, longitude } = loc || {};
  const txt = [address, line1, area, city, state, pincode].map(x => (x || "").toString().trim()).filter(Boolean).join(", ");
  if (txt) return txt;
  const la = lat ?? latitude, lo = lng ?? longitude;
  return la != null && lo != null ? `${la}, ${lo}` : "N/A";
};

const orDash = (v) => (v === 0 ? "0" : (v == null || String(v).trim() === "" ? "—" : v));

/* ---------------- component ---------------- */
const EmployeeReport = () => {
  const [requirements, setRequirements] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState("");
  const [reqInfo, setReqInfo] = useState(null);

  const [availability, setAvailability] = useState(""); // "yes" | "no" | ""
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");

  // core fields
  const [core, setCore] = useState({
    budget: "",
    process: "",
    items: "",
    message: "",
  });

  // rich fields
  const [rich, setRich] = useState({
    availableQty: "",
    pricePerUnit: "",
    currency: "INR",
    moq: "",
    leadTimeDays: "",
    availableFrom: "",
    paymentTerms: "Advance",
    transportMode: "By Road",
    deliveryMode: "Pickup",
    deliveryLocationOverride: "",
  });

  /* ------------- load my requirements ------------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await API.get("/employee/my-requirements");
        setRequirements(Array.isArray(res.data) ? res.data : []);
      } catch {
        setToast("❌ Failed to load requirements");
      }
    })();
  }, []);

  /* ------------- keep selected req info ------------- */
  useEffect(() => {
    if (!selectedRequirement) {
      setReqInfo(null);
      setAvailability("");
      return;
    }
    const req = requirements.find((r) => r._id === selectedRequirement);
    setReqInfo(req || null);
  }, [selectedRequirement, requirements]);

  const vendorBlock = useMemo(() => {
    if (!reqInfo) return null;
    const v = reqInfo.vendor || {};
    const fall = reqInfo.vendorId || {};
    const u = fall.userId || {};
    return {
      company: v.companyName || fall.companyName || "N/A",
      name: v.fullName || u.fullName || "-",
      mobile: v.mobile || u.mobile || "-",
      email: v.email || u.email || "-",
      gst: v.gstNumber || fall.gstNumber || "-",
      address: v.address || fall.address || "-",
    };
  }, [reqInfo]);

  /* ------------- submit ------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedRequirement) {
      setToast("⚠ Please select a requirement.");
      return;
    }
    if (!availability) {
      setToast("⚠ Please choose availability (Yes / No).");
      return;
    }

    try {
      setLoading(true);

      // ✅ requirementId include kiya gaya hai
      const payload = {
        requirementId: selectedRequirement,
        hasRequirement: availability, // "yes" | "no"
        message: core.message,
        extra: { ...rich },           // rich details
      };

      if (availability === "yes") {
        payload.budget = core.budget;
        payload.process = core.process;
        payload.items = core.items;
      }

      // Use this if your backend route is /employee/reports
      const res = await API.post("/employee/reports", payload);
      // If you use /reports instead:
      // const res = await API.post("/reports", payload);

      setToast(res.data?.message || "✅ Report submitted successfully!");

      // reset
      setAvailability("");
      setSelectedRequirement("");
      setReqInfo(null);
      setCore({ budget: "", process: "", items: "", message: "" });
      setRich({
        availableQty: "",
        pricePerUnit: "",
        currency: "INR",
        moq: "",
        leadTimeDays: "",
        availableFrom: "",
        paymentTerms: "Advance",
        transportMode: "By Road",
        deliveryMode: "Pickup",
        deliveryLocationOverride: "",
      });
    } catch (err) {
      setToast(err.response?.data?.message || "❌ Failed to submit report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-4">
      <Card className="p-4 shadow-sm">
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="mb-0">📝 Submit Requirement Report</h4>
          {availability && (
            <Badge bg={availability === "yes" ? "success" : "secondary"}>
              {availability === "yes" ? "Available" : "Not Available"}
            </Badge>
          )}
        </div>

        {toast && <Alert variant="info" className="mt-3">{toast}</Alert>}

        <Form onSubmit={handleSubmit} className="mt-3">
          {/* ---------- Requirement Selector ---------- */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Select Requirement</Form.Label>
            <Form.Select
              value={selectedRequirement}
              onChange={(e) => setSelectedRequirement(e.target.value)}
            >
              <option value="">— Select Requirement —</option>
              {requirements.map((r) => (
                <option key={r._id} value={r._id}>
                  {r.productType} • {r.quantity ?? "-"} {r.unit || ""} (
                  {r.vendor?.companyName || r.vendorId?.companyName || "Vendor"})
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* ---------- Requirement & Vendor Preview ---------- */}
          {reqInfo && (
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Card className="h-100">
                  <Card.Body>
                    <Card.Title className="mb-3">📦 Requirement</Card.Title>
                    <div className="mb-1"><strong>Product:</strong> {orDash(reqInfo.productType)}</div>
                    <div className="mb-1"><strong>Quantity:</strong> {orDash(reqInfo.quantity)} {orDash(reqInfo.unit)}</div>
                    <div className="mb-1"><strong>Location:</strong> {locationText(reqInfo)}</div>
                    <div className="mb-1"><strong>Notes:</strong> {orDash(reqInfo.notes)}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="h-100">
                  <Card.Body>
                    <Card.Title className="mb-3">🏢 Vendor</Card.Title>
                    <div className="mb-1"><strong>Company:</strong> {vendorBlock?.company}</div>
                    <div className="mb-1"><strong>Contact:</strong> {vendorBlock?.name}</div>
                    <div className="mb-1"><strong>Mobile:</strong> {vendorBlock?.mobile}</div>
                    <div className="mb-1"><strong>Email:</strong> {vendorBlock?.email}</div>
                    <div className="mb-1"><strong>GST:</strong> {vendorBlock?.gst}</div>
                    <div className="mb-1"><strong>Address:</strong> {vendorBlock?.address}</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* ---------- Availability ---------- */}
          {reqInfo && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Requirement Available?</Form.Label>
              <div>
                <Form.Check
                  inline type="radio" name="reqAvail" id="avail-yes" value="yes"
                  label="Yes" checked={availability === "yes"}
                  onChange={(e) => setAvailability(e.target.value)}
                />
                <Form.Check
                  inline type="radio" name="reqAvail" id="avail-no" value="no"
                  label="No" checked={availability === "no"}
                  onChange={(e) => setAvailability(e.target.value)}
                />
              </div>
            </Form.Group>
          )}

          {/* ---------- Rich section when available ---------- */}
          {availability === "yes" && (
            <>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Available Quantity</Form.Label>
                    <Form.Control
                      placeholder="e.g., 5000"
                      value={rich.availableQty}
                      onChange={(e) => setRich({ ...rich, availableQty: e.target.value })}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>MOQ</Form.Label>
                    <Form.Control
                      placeholder="Minimum order quantity"
                      value={rich.moq}
                      onChange={(e) => setRich({ ...rich, moq: e.target.value })}
                    />
                  </Form.Group>
                </Col>

                <Col md={7}>
                  <Form.Group>
                    <Form.Label>Price Per Unit</Form.Label>
                    <InputGroup>
                      <Form.Control
                        placeholder="e.g., 52.5"
                        value={rich.pricePerUnit}
                        onChange={(e) => setRich({ ...rich, pricePerUnit: e.target.value })}
                      />
                      <Form.Select
                        style={{ maxWidth: 120 }}
                        value={rich.currency}
                        onChange={(e) => setRich({ ...rich, currency: e.target.value })}
                      >
                        <option>INR</option>
                        <option>USD</option>
                        <option>EUR</option>
                      </Form.Select>
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Lead Time (days)</Form.Label>
                    <Form.Control
                      placeholder="e.g., 7"
                      value={rich.leadTimeDays}
                      onChange={(e) => setRich({ ...rich, leadTimeDays: e.target.value })}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Available From</Form.Label>
                    <Form.Control
                      type="date"
                      value={rich.availableFrom}
                      onChange={(e) => setRich({ ...rich, availableFrom: e.target.value })}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Payment Terms</Form.Label>
                    <Form.Select
                      value={rich.paymentTerms}
                      onChange={(e) => setRich({ ...rich, paymentTerms: e.target.value })}
                    >
                      <option>Advance</option>
                      <option>50% Advance / 50% on Delivery</option>
                      <option>Net 7</option>
                      <option>Net 15</option>
                      <option>Net 30</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Transport Mode</Form.Label>
                    <Form.Select
                      value={rich.transportMode}
                      onChange={(e) => setRich({ ...rich, transportMode: e.target.value })}
                    >
                      <option>By Road</option>
                      <option>By Rail</option>
                      <option>By Air</option>
                      <option>By Sea</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Delivery Mode</Form.Label>
                    <Form.Select
                      value={rich.deliveryMode}
                      onChange={(e) => setRich({ ...rich, deliveryMode: e.target.value })}
                    >
                      <option>Pickup</option>
                      <option>Delivery</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Delivery Location (override)</Form.Label>
                    <Form.Control
                      placeholder="If delivery address differs, mention here"
                      value={rich.deliveryLocationOverride}
                      onChange={(e) => setRich({ ...rich, deliveryLocationOverride: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr className="my-4" />

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Budget (total)</Form.Label>
                    <Form.Control
                      value={core.budget}
                      onChange={(e) => setCore({ ...core, budget: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Process / Next Steps</Form.Label>
                    <Form.Control
                      value={core.process}
                      onChange={(e) => setCore({ ...core, process: e.target.value })}
                      placeholder="e.g., in negotiation / in shipping"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Items (brands / varieties)</Form.Label>
                    <Form.Control
                      value={core.items}
                      onChange={(e) => setCore({ ...core, items: e.target.value })}
                      placeholder="e.g., Lotus, 1121 Basmati, etc."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {/* ---------- Message (always) ---------- */}
          <Form.Group className="mt-3">
            <Form.Label>Message / Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={core.message}
              onChange={(e) => setCore({ ...core, message: e.target.value })}
              placeholder="Any additional notes, constraints, or clarifications"
            />
          </Form.Group>

          {/* ---------- Submit ---------- */}
          {availability && (
            <div className="mt-4">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : "Submit Report"}
              </Button>
            </div>
          )}
        </Form>
      </Card>
    </Container>
  );
};

export default EmployeeReport;
